import React, { useState } from 'react';
import { Alert, Box, CircularProgress, TextField, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import ListProposalCard from './ListProposalCard';
import PoolSection from './PoolSection';
import HiddenFold from './HiddenFold';
import { ColorDot, SeasonBadge, StatStrip } from './Badges';
import { TierBadge } from './OrderLineCard';
import { VetoControls, excludedStyles, planVetoKey } from './exclusions';
import { SinsangLink, SinsangNotice, Sourcing } from './SinsangLinks';
import {
  ADD_BACK_LABEL, ADD_BUTTON_LABEL, ADD_INPUT_LABEL, ADD_NOTE, ADD_PLACEHOLDER, ADD_TITLE,
  ADD_UNDO_LABEL,
  LIST_ADD_BACK_LABEL, LIST_ADD_LABEL, LIST_ALL_VETOED_BADGE, LIST_BATCH_LABEL, LIST_BATCH_NOTE,
  LIST_EMPTY_NOTE,
  LIST_NOT_READY, LIST_NO_PROPOSALS, LIST_REMOVE_LABEL, LIST_SEARCH_EMPTY,
  LIST_SEED_LABEL, LIST_STOCK_NOTE, LIST_STOCK_TITLE, LIST_NO_LINK_WARNING,
  LIST_MAIN_BADGE, LIST_MAIN_LABEL, LIST_MAIN_NOTE, LIST_UNMAIN_LABEL,
  LIST_VARIANT_FOLD_CLOSE, LIST_VARIANT_FOLD_OPEN,
  LIST_TOP_BADGE, LIST_TOP_NOTE, LIST_TOP_NOTE_FALLBACK,
  MAIN_BATCH_KEY, MAIN_BATCH_LABEL, MAIN_BATCH_NOTE, mainBatchPicks, mainVariantIds,
  LIST_VARIANT_EXCLUDED_NOTE, LIST_VARIANT_NOTE, LIST_VARIANT_REJECT_LABEL, approvedRows,
  bringBackHints, cappedQueue, engineProposals, excludedVariants, groupApprovedByFixture,
  isFreeToAdd,
  isOverCapacity, isSourced, listVariants, missingSkus, overLine, plannedLine, proposalEvidence,
  proposalsToRemove, railLabel, searchRowState, seasonActionLine, stockNote, stockProposals,
  variantMainPendingKey, variantPendingKey, variantsReady,
} from './counterList';
import { accountingListLine } from './accounting';
import { CounterList, ADD_KEY, BATCH_KEY, ManualAdd, SEED_KEY } from './useCounterList';
import { CounterPool } from './useCounterPool';
import { displayTitle, fixtureLabelOf, thumbTarget, variantChipLabel } from './format';
import { C, MONO, RADIUS, SHADOW } from './tokens';
import {
  RestockV2List, RestockV2ListApproved, RestockV2ListSearchRow, RestockV2ListVariant,
  RestockV2Plan,
  RestockV2ThumbTarget,
} from './types';

// 選品 — the screen where Magnus owns the assortment instead of the engine.
//
//   1  header strip: 清單 N 款 · 計畫 P／C 件 (red + 超出 N 件 when over) · 秋冬 X% 目標 Y%
//   2  加入商品: type a SKU, press Enter, it is on the list. HIS move, so it sits above the engine's
//      queues — it used to be at the bottom of the screen and he could not find it (2026-09-18).
//      Typing a NAME instead falls through to the picker, which is the same box and same endpoint.
//   3  建議加入 / 建議換下: the yes/no queue, one card each, 依據 always on the face of the card,
//      plus a 全部照建議 batch that is ADDITIVE ONLY (a 全選／全部取消 was removed from 訂貨 on
//      2026-09-15 — one mis-tap there vetoed the whole order for Adam too).
//   4  我的清單: the approved list, grouped the way 衣架 groups, with 在櫃／不在櫃 and 移除.
//   5  已排除: the folded 不要這款 list, 加回 in one tap.
//
// Over capacity is a WARNING, never a gate: it shows the overflow and the engine's ranked 建議帶回,
// and disables nothing.

interface ListScreenProps {
  plan: RestockV2Plan;
  list: RestockV2List | null;
  writes: CounterList;
  // 看更多 — the ranked pool BELOW the engine's bar. Its own endpoint and its own paging state, so a
  // /restock-v2 recompute landing mid-scroll costs neither his place nor his answers.
  pool: CounterPool;
  // The chips' two verbs live with the other veto verbs, so a colour struck here and a colour
  // struck on a rack tile share ONE optimistic store and one busy map.
  veto: VetoControls & { onReadd: (sku: string, variantId: number) => void };
  // ≥1024 px: two proposal cards per row. ≥1440 px: three. The page is mostly used on the office PC.
  desktop: boolean;
  wide: boolean;
  // The same Sourcing 訂貨 uses, built by the tab from ONE useSourcing call — one capability check,
  // one stored-link lookup, one 門市人員 gate for both screens.
  sourcing: Sourcing;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const Head: React.FC<{ title: string; note?: string; count?: string; children?: React.ReactNode }> = ({
  title, note, count, children,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: '2px', pb: 0.9, pt: 2.25 }}>
    <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900, letterSpacing: '-.01em' }}>
      {title}
      {note && <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '5px' }}>{note}</Box>}
    </Typography>
    {count && (
      <Typography
        sx={{
          ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.ink2,
          bgcolor: C.paper2, borderRadius: '99px', px: 1,
        }}
      >
        {count}
      </Typography>
    )}
    {children}
  </Box>
);

const Grid: React.FC<{ columns: number; children: React.ReactNode }> = ({ columns, children }) => (
  <Box
    sx={{
      display: 'grid', gap: 1,
      ...(columns > 1
        ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
        : { '@media (min-width:600px)': { gridTemplateColumns: '1fr 1fr' } }),
      alignItems: 'stretch',
    }}
  >
    {children}
  </Box>
);

// 「還有 37 款 · 顯示全部」. Nothing is hidden silently: the button says how many are behind it.
const MoreButton: React.FC<{ hidden: boolean; total: number; onClick: () => void }> = ({
  hidden, total, onClick,
}) => {
  if (hidden) return null;
  return (
    <Box
      component="button" type="button" onClick={onClick} aria-label={`顯示全部 ${total} 款`}
      sx={{
        mt: 1, width: '100%', py: 1, cursor: 'pointer', border: '1px dashed', borderColor: C.line2,
        bgcolor: C.wash, borderRadius: '9px', fontSize: 12.5, fontWeight: 700, color: C.ink2,
      }}
    >
      顯示全部 {total} 款
    </Box>
  );
};

// 不要這個顏色／尺寸 — Magnus 2026-09-17: 「when i delete a 款式 不要這款 it removes all sku for this
// product but i actually just don't want a specific color or size」.
//
// The chips live on the 我的清單 row because that is where he curates. Same chip as 訂貨 (ColorDot +
// 「黑色 L」 + a mono count), plus one control per chip:
//   live   → ✕, one tap, no confirm. It is one colour and the chip itself is the undo.
//   vetoed → the chip STAYS, struck through and greyed, carrying 加回. Nothing is hidden: he has to
//            be able to see what he took out, or the only way to find it is to remember it.
const VariantChips: React.FC<{
  row: RestockV2ListApproved;
  ready: boolean;
  busy: (variantId: number) => boolean;
  onReject: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  onReadd: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  // 設為主打 lives HERE and not on the 訂貨 card, deliberately. The 訂貨 card only carries the
  // colours it is ordering — on 2732 that is 黑色 alone, so the colour he actually wants to pin
  // (海軍藍) has no chip there to tap. This row carries every live colour, and it is already
  // where the other standing per-colour opinion (不要這個顏色) is expressed. The 訂貨 card answers
  // 「why this colour」 and points here; it never becomes a second write path.
  mainsReady: boolean;
  mainBusy: (variantId: number | string | null) => boolean;
  // SEVERAL since 2026-09-20 (Magnus: 「should be able to set more than 1 主打」). A style's
  // 主打 are the colours it carries, not one favourite.
  pinnedIds: string[];
  onMain: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  onUnmain: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
}> = ({ row, ready, busy, onReject, onReadd, mainsReady, mainBusy, pinnedIds, onMain, onUnmain }) => {
  const all = listVariants(row);
  // 建議的顏色／尺寸 (Magnus 2026-09-19: 「the default should not be all color and 尺寸」). The
  // screen rendered 727 live chips over 97 styles, one style carrying 44 — a wall he cannot
  // curate. The server ranks them and marks the ones worth showing (`suggested`), always
  // including anything ordered, owned, pinned or 留著; the rest fold behind 看全部.
  //
  // A VETOED chip never folds. It is his own decision and the chip is the only way back, so it
  // stays where he can see it — the rule this whole feature is built around is that nothing is
  // hidden, only quieter. `suggested` absent (an older server) shows everything, as before.
  const [open, setOpen] = React.useState(false);
  const folded = all.filter((v) => v.suggested === false && v.vetoed !== true);
  const variants = open || !folded.length ? all : all.filter((v) => !folded.includes(v));
  // No chip line at all when the engine cannot name the colours: a 「單一款 ✕」 on a style with
  // unknown variants would offer a veto against nothing.
  if (!all.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', mt: '5px', minWidth: 0 }}>
      {variants.map((variant) => {
        const vetoed = variant.vetoed === true;
        const pinned = pinnedIds.includes(String(variant.variant_id));
        const isTop = row.top_variant != null
          && String(row.top_variant.variant_id) === String(variant.variant_id);
        const chip = variantChipLabel(variant);
        const target = Number(variant.target);
        const pending = busy(variant.variant_id);
        return (
          <Box
            key={`${row.sku}:${variant.variant_id}`} component="span"
            data-variant={`${row.sku}:${variant.variant_id}`}
            data-vetoed={vetoed ? 'true' : 'false'}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '1px', maxWidth: '100%',
              fontSize: 12, fontWeight: 700, borderRadius: '8px', pl: '6px',
              pr: ready ? '2px' : '6px', py: '1px',
              whiteSpace: 'nowrap',
              // A vetoed chip has to be legible AS vetoed from a metre away, not on inspection: the
              // strike alone was nearly invisible at 1x against a chip one shade off the live one.
              // Dashed border + the flat paper fill reads as 「out」 before the words are read.
              bgcolor: vetoed ? C.paper2 : C.card,
              border: vetoed ? '1px dashed' : '1px solid',
              borderColor: vetoed ? C.line2 : C.line, color: vetoed ? C.ink3 : C.ink,
              opacity: pending ? 0.55 : 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex', alignItems: 'center', minWidth: 0, overflow: 'hidden',
                textOverflow: 'ellipsis', textDecoration: vetoed ? 'line-through' : 'none',
              }}
            >
              <ColorDot name={variant.color} size={9} />
              {chip}
              {!vetoed && Number.isFinite(target) && target > 0 && (
                <Box component="span" sx={{ fontFamily: MONO, ml: '3px', color: C.ink3 }}>×{target}</Box>
              )}
              {pinned && (
                <Box
                  component="span" data-main-badge={`${row.sku}:${variant.variant_id}`}
                  sx={{
                    ml: '4px', px: '4px', py: 0, borderRadius: '5px', fontSize: 10, fontWeight: 800,
                    bgcolor: C.goWash, color: C.go, border: '1px solid', borderColor: C.go,
                  }}
                >
                  {LIST_MAIN_BADGE}
                </Box>
              )}
              {/* 熱銷 — what the ranking already decided, so the answer is on the row instead of
                  behind a tap. Never shown next to 主打 on the same chip: his decision and the
                  measurement would read as one claim, and it is the difference between them
                  that the pin is for. Quieter than 主打 (a wash, no border) for the same reason —
                  it is information, not a decision. */}
              {!vetoed && !pinned && isTop && (
                <Box
                  component="span" data-top-badge={`${row.sku}:${variant.variant_id}`}
                  title={(row.top_variant?.basis && LIST_TOP_NOTE[row.top_variant.basis])
                    || LIST_TOP_NOTE_FALLBACK}
                  sx={{
                    ml: '4px', px: '4px', py: 0, borderRadius: '5px', fontSize: 10, fontWeight: 800,
                    bgcolor: C.paper2, color: C.ink2,
                  }}
                >
                  {LIST_TOP_BADGE}
                </Box>
              )}
            </Box>
            {/* A pin is a standing opinion like the veto beside it, so it lives on the same chip.
                Offered only on live colours: pinning a colour he has excluded is an incoherent
                pair, and the server refuses it 409 — better not to show the button at all. */}
            {mainsReady && !vetoed && (
              <Box
                component="button" type="button" disabled={mainBusy(variant.variant_id)}
                onClick={() => (pinned ? onUnmain(row, variant) : onMain(row, variant))}
                title={LIST_MAIN_NOTE}
                aria-label={`${pinned ? LIST_UNMAIN_LABEL : LIST_MAIN_LABEL} ${row.sku} ${chip}`}
                sx={{
                  flex: 'none', appearance: 'none', bgcolor: 'transparent', border: 0, ml: '2px',
                  px: '3px', py: 0, lineHeight: 1, fontSize: 11, fontWeight: 800,
                  color: pinned ? C.go : C.ink3, cursor: 'pointer', borderRadius: '6px',
                  '&:hover': { bgcolor: C.goWash, color: C.go },
                  '&:disabled': { opacity: 0.5, cursor: 'default' },
                }}
              >
                {pinned ? LIST_UNMAIN_LABEL : LIST_MAIN_LABEL}
              </Box>
            )}
            {/* Not migrated yet: the chips still SHOW what the engine plans, but neither verb is
                offered — a ✕ that can only answer 503 teaches him the screen is broken. */}
            {!ready ? null : vetoed ? (
              <Box
                component="button" type="button" disabled={pending}
                onClick={() => onReadd(row, variant)}
                aria-label={`${LIST_ADD_BACK_LABEL} ${row.sku} ${chip}`}
                sx={{
                  // A pill, not bare text: 「米白 F 加回」 read as one phrase until the verb had an
                  // edge of its own.
                  flex: 'none', appearance: 'none', bgcolor: C.goWash, border: '1px solid',
                  borderColor: C.go, borderRadius: '6px', ml: '4px', mr: '2px', px: '5px',
                  py: '1px', fontSize: 11, fontWeight: 800, color: C.go, cursor: 'pointer',
                  '&:disabled': { opacity: 0.5, cursor: 'default' },
                }}
              >
                {LIST_ADD_BACK_LABEL}
              </Box>
            ) : (
              <Box
                component="button" type="button" disabled={pending}
                onClick={() => onReject(row, variant)}
                aria-label={`${LIST_VARIANT_REJECT_LABEL} ${row.sku} ${chip}`}
                sx={{
                  flex: 'none', appearance: 'none', bgcolor: 'transparent', border: 0, width: 20,
                  height: 20, lineHeight: 1, fontSize: 12, fontWeight: 800, color: C.ink3,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', borderRadius: '99px',
                  '&:hover': { bgcolor: C.backWash, color: C.back },
                  '&:disabled': { opacity: 0.5, cursor: 'default' },
                }}
              >
                ✕
              </Box>
            )}
          </Box>
        );
      })}
      {folded.length > 0 && (
        <Box
          component="button" type="button" data-variant-fold={row.sku}
          onClick={() => setOpen((value) => !value)}
          sx={{
            flex: 'none', appearance: 'none', bgcolor: 'transparent', border: '1px dashed',
            borderColor: C.line2, borderRadius: '8px', px: '7px', py: '1px', fontSize: 11.5,
            fontWeight: 700, color: C.ink3, cursor: 'pointer', whiteSpace: 'nowrap',
            '&:hover': { color: C.ink2, borderColor: C.line },
          }}
        >
          {open ? LIST_VARIANT_FOLD_CLOSE : `${LIST_VARIANT_FOLD_OPEN} ${folded.length}`}
        </Box>
      )}
    </Box>
  );
};

// One row of 我的清單. Compact by design: 80 of these is the normal case, so a row is a line, not a
// card. The owner_pick badge is the honest note that HE put it there and the engine did not.
const ApprovedRow: React.FC<{
  row: RestockV2ListApproved; pending: boolean; onRemove: (sku: string) => void;
  variantsReady: boolean;
  variantBusy: (sku: string, variantId: number) => boolean;
  onRejectVariant: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  onReaddVariant: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  mainsReady: boolean;
  mainBusy: (variantId: number | string | null) => boolean;
  pinnedIds: string[];
  onMainVariant: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  onUnmainVariant: (row: RestockV2ListApproved, variant: RestockV2ListVariant) => void;
  sourcing: Sourcing;
  onImage: (target: RestockV2ThumbTarget) => void;
}> = ({
  row, pending, onRemove, variantsReady, variantBusy, onRejectVariant, onReaddVariant,
  mainsReady, mainBusy, pinnedIds, onMainVariant, onUnmainVariant, sourcing,
  onImage,
}) => (
  <Box
    component="article" data-approved={row.sku}
    sx={{
      display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: C.card, border: '1px solid',
      borderColor: C.line, borderRadius: '10px', p: 0.9, boxShadow: SHADOW, minWidth: 0,
    }}
  >
    <ProductThumb
      target={thumbTarget({ sku: row.sku, title: row.title, image_url: row.image_url ?? null })}
      width={46} height={61} radius={6} onOpen={onImage}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
        {/* An approved style the engine did not pick has no tier — no chip beats a wrong one. */}
        {row.tier ? <TierBadge tier={row.tier} /> : null}
        <SeasonBadge value={row.season} size="xs" />
        {row.owner_pick && (
          <Box
            component="span"
            sx={{
              fontSize: 10, fontWeight: 800, borderRadius: '99px', px: '6px', mb: '3px',
              bgcolor: C.fetchWash, color: C.fetch, whiteSpace: 'nowrap',
            }}
          >
            你指定
          </Box>
        )}
        {/* He vetoed every colour one at a time. The row stays — its chips are the only way back —
            and the server's own lane_reason underneath says what that now costs the style. */}
        {row.all_variants_vetoed && (
          <Box
            component="span"
            sx={{
              fontSize: 10, fontWeight: 800, borderRadius: '99px', px: '6px', mb: '3px',
              bgcolor: C.backWash, color: C.back, whiteSpace: 'nowrap',
            }}
          >
            {LIST_ALL_VETOED_BADGE}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>
        {displayTitle(row.title)}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', mt: '2px' }}>
        <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
        <Typography
          component="span"
          sx={{ fontSize: 11, fontWeight: 700, color: row.on_rail ? C.go : C.ink3 }}
        >
          {railLabel(row.on_rail)}
        </Typography>
        <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11.5, color: C.ink2 }}>
          {row.depth} 件
        </Typography>
      </Box>
      {/* 依據 stays visible here too — 我的清單 is where he audits his own past yes/no. */}
      {row.lane_reason && (
        <Typography sx={{ fontSize: 11.5, color: C.ink3, mt: '2px', lineHeight: 1.4 }}>{row.lane_reason}</Typography>
      )}
      {/* Magnus 2026-09-17: 「also should have sinsang link at the list」. An approved style is one
          he may want to look up or order, so the same 下單連結 訂貨 carries belongs on the row —
          the SAME `sourcing-links` request and the SAME 門市人員 gate (useSourcing), never a second
          mechanism and never a second permission path.

          The two states are mutually exclusive by construction: a row with no 下單連結 keeps saying
          訂不到 and is not passed to SinsangLink at all, so 選品 can never offer a dead link. (And
          SinsangLink itself renders nothing for a SKU with no stored URL, so a row whose link is
          not in THIS lookup degrades to no link rather than to a fabricated one.)

          `isSourced` keys on the 下單連結 and the 台灣倉庫 lane, never on the 進貨店家 tag, so an
          MdoC row lands in the link branch and cannot print 訂不到 here. */}
      {isSourced(row) ? (
        <SinsangLink sourcing={sourcing} sku={row.sku} />
      ) : (
        <Typography sx={{ fontSize: 11, color: C.back, fontWeight: 700, mt: '2px' }}>
          {LIST_NO_LINK_WARNING}
        </Typography>
      )}
      <VariantChips
        row={row} ready={variantsReady} busy={(variantId) => variantBusy(row.sku, variantId)}
        onReject={onRejectVariant} onReadd={onReaddVariant}
        mainsReady={mainsReady} mainBusy={mainBusy} pinnedIds={pinnedIds}
        onMain={onMainVariant} onUnmain={onUnmainVariant}
      />
    </Box>
    <Box
      component="button" type="button" onClick={() => onRemove(row.sku)} disabled={pending}
      aria-label={`${LIST_REMOVE_LABEL} ${row.sku}`}
      sx={{
        flex: 'none', appearance: 'none', bgcolor: 'transparent', border: 0, p: 0, fontSize: 11.5,
        fontWeight: 800, color: C.back, cursor: 'pointer', textDecoration: 'underline',
        textUnderlineOffset: '2px', '&:disabled': { opacity: 0.5, cursor: 'default' },
      }}
    >
      {LIST_REMOVE_LABEL}
    </Box>
  </Box>
);

// One hit of the 加入商品 picker — the SECONDARY path, for when he does not know the SKU. A row with
// no 下單連結 is still addable; it just says so.
const SearchRow: React.FC<{
  row: RestockV2ListSearchRow; pending: boolean; onAdd: (sku: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ row, pending, onAdd, onImage }) => {
  const state = searchRowState(row);
  const sourced = isSourced(row);
  // What 加入 costs him: a style already at the counter is a free decision, one with nothing on
  // hand puts pieces on the next Korea order. Said before the tap, not after the order is copied.
  const stock = stockNote(row);
  return (
    <Box
      component="article" data-search={row.sku}
      sx={{
        display: 'flex', gap: 1, alignItems: 'center', bgcolor: C.card, border: '1px solid',
        borderColor: C.line, borderRadius: '10px', p: 0.9, mb: 0.75, minWidth: 0,
      }}
    >
      <ProductThumb
        target={thumbTarget({ sku: row.sku, title: row.title, image_url: row.image_url ?? null })}
        width={40} height={53} radius={6} onOpen={onImage}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px' }}>
          <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-.01em' }}>
            {displayTitle(row.title)}
          </Typography>
          <SeasonBadge value={row.season} size="xs" />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', mt: '2px' }}>
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
          {row.cat && <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.cat}</Typography>}
          <Typography
            component="span"
            sx={{ fontSize: 11, fontWeight: 700, color: sourced ? C.go : C.back }}
          >
            {sourced ? `訂得到${row.source_tag ? ` · ${row.source_tag}` : ''}` : LIST_NO_LINK_WARNING}
          </Typography>
          {stock && (
            <Typography
              component="span"
              sx={{ fontSize: 11, fontWeight: 700, color: isFreeToAdd(row) ? C.go : C.ink3 }}
            >
              {stock}
            </Typography>
          )}
        </Box>
      </Box>
      {state && (
        <Typography
          component="span"
          sx={{
            flex: 'none', fontSize: 11, fontWeight: 800, color: C.ink2, bgcolor: C.paper2,
            borderRadius: '99px', px: '7px',
          }}
        >
          {state}
        </Typography>
      )}
      <Box
        component="button" type="button" onClick={() => onAdd(row.sku)}
        disabled={pending || row.on_list === 'approved'}
        aria-label={`${LIST_ADD_LABEL} ${row.sku}`}
        sx={{
          flex: 'none', appearance: 'none', cursor: 'pointer', borderRadius: '8px', px: 1.2, py: '6px',
          fontSize: 12.5, fontWeight: 800, border: '1px solid', borderColor: C.go, bgcolor: C.goWash,
          color: C.go, '&:disabled': { opacity: 0.45, cursor: 'default' },
        }}
      >
        {LIST_ADD_LABEL}
      </Box>
    </Box>
  );
};

// The five honest answers, and the tone each deserves. Only 'added' is a success and only two of
// the others are failures — 已在清單 and 已排除 are decisions of his own coming back, so painting
// them red would be telling him off for remembering his own list.
const OUTCOME_TONE: Record<ManualAdd['kind'], { colour: string; wash: string; border: string }> = {
  added: { colour: C.go, wash: C.goWash, border: C.go },
  already: { colour: C.ink2, wash: C.paper2, border: C.line2 },
  rejected: { colour: C.wait, wash: C.waitWash, border: C.wait },
  delisted: { colour: C.back, wash: C.backWash, border: C.back },
  missing: { colour: C.back, wash: C.backWash, border: C.back },
  error: { colour: C.back, wash: C.backWash, border: C.back },
  hits: { colour: C.ink2, wash: C.wash, border: C.line },
};

/**
 * 加入商品 — 「type a SKU, press Enter, it is on the list」 (Magnus 2026-09-18).
 *
 * WHY IT IS HERE, ABOVE THE QUEUES. This existed already, at the BOTTOM of 選品 under 你已經有貨 /
 * 建議加入 / 看更多 / 建議換下, and he could not find it — with 看更多 open it is thousands of pixels
 * down. A control he cannot find is a control that does not exist, so placement is the fix, not
 * extra copy. It sits directly under the header numbers and the two header notes because those are
 * STATUS (what the list is) and this is the one thing on the screen he can do without the engine
 * having proposed anything first. The four queues below are the engine's agenda; this is his.
 *
 * It replaces the old section rather than joining it: there is exactly ONE manual-add mechanism on
 * the screen. The picker itself is not gone — it is the SECONDARY path, reached by typing a name
 * instead of a SKU, same box, same endpoint, same 加入 button on each hit.
 *
 * WHAT IT COSTS TO KEEP IT SHORT. 選品 is already the densest screen in the tab, so the resting
 * state is three lines: a heading, the box, one hint. Everything else appears only after he has
 * pressed Enter, and exactly one outcome is on screen at a time.
 */
const AddBySku: React.FC<{
  writes: CounterList;
  desktop: boolean;
  onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ writes, desktop, onImage }) => {
  const [query, setQuery] = useState('');
  const [outcome, setOutcome] = useState<ManualAdd | null>(null);
  // Local, because the resolve spans a GET and a POST and neither hook flag covers both. It is the
  // ONLY await in the click path that the screen waits on; the add underneath is optimistic and the
  // recompute behind it is debounced (useCounterList.write).
  const [resolving, setResolving] = useState(false);

  const run = async () => {
    if (!query.trim() || resolving) return;
    setResolving(true);
    setOutcome(null);
    try {
      const answer = await writes.addBySku(query);
      if (!answer) return;
      setOutcome(answer);
      // Clear the box ONLY when the SKU landed on the list. On every other answer he still needs
      // what he typed — to fix a typo, to read it back, or to press 加回清單.
      if (answer.kind === 'added') setQuery('');
    } finally {
      setResolving(false);
    }
  };

  // 加回清單 on a style he rejected before: an explicit second tap, never automatic. The add is the
  // same write and flips rejected → approved in one UPSERT.
  const addBack = async (sku: string) => {
    const answer = await writes.addBySku(sku);
    if (answer) setOutcome(answer);
    setQuery('');
  };

  // 取消加入 — the undo. `remove` DELETEs the row, which is what puts the style back to 待決定, so
  // the engine may propose it again; it is the same verb 我的清單's 移除 uses. The card goes at once
  // because he knows what he just pressed.
  const undo = (sku: string) => {
    setOutcome(null);
    writes.remove(sku);
  };

  const tone = outcome ? OUTCOME_TONE[outcome.kind] : null;
  const busySku = outcome && (!!writes.pending[outcome.sku] || !!writes.pending[ADD_KEY]);

  return (
    <Box component="section" aria-label={ADD_TITLE} sx={{ mb: 1.5 }}>
      <Head title={ADD_TITLE} note="引擎沒建議、但你想放的款" />
      <Box sx={{ display: 'flex', gap: 1, maxWidth: desktop ? 520 : undefined }}>
        <TextField
          size="small" fullWidth value={query} disabled={resolving}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); run(); } }}
          placeholder={ADD_PLACEHOLDER}
          inputProps={{ 'aria-label': ADD_INPUT_LABEL }}
        />
        <Box
          component="button" type="button" onClick={run} disabled={resolving || !query.trim()}
          aria-label={ADD_BUTTON_LABEL}
          sx={{
            flex: 'none', appearance: 'none', cursor: 'pointer', borderRadius: '9px', px: 2,
            fontSize: 13.5, fontWeight: 800, border: 0, bgcolor: C.go, color: '#fff',
            whiteSpace: 'nowrap', '&:disabled': { opacity: 0.5, cursor: 'default' },
          }}
        >
          {ADD_BUTTON_LABEL}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11.5, color: C.ink3, px: '2px', pt: 0.6, lineHeight: 1.45 }}>
        {ADD_NOTE}
      </Typography>

      {resolving && <Box role="status" aria-label="查詢中" sx={{ py: 1 }}><CircularProgress size={18} /></Box>}
      {writes.searchError && <Alert severity="error" sx={{ mt: 1 }}>{writes.searchError}</Alert>}

      {/* ONE outcome, never a stack. `data-outcome` is the kind, so a test asserts which of the five
          answers this is rather than pattern-matching the sentence. */}
      {outcome && tone && !resolving && (
        <Box
          role="status" data-outcome={outcome.kind} aria-label={`加入結果 ${outcome.kind}`}
          sx={{
            display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1, p: 0.9, minWidth: 0,
            bgcolor: tone.wash, border: '1px solid', borderColor: tone.border,
            borderRadius: `${RADIUS}px`, maxWidth: desktop ? 520 : undefined,
          }}
        >
          {/* The thumbnail is the point of the confirmation: he typed four digits, and the only way
              to know those four digits were the style he meant is to SEE it. A blind add (nothing in
              the picker) has no row, so it shows the title the write itself returned and no image —
              never a placeholder standing in for a garment nobody looked up. */}
          {outcome.row && (
            <ProductThumb
              target={thumbTarget({
                sku: outcome.row.sku, title: outcome.row.title, image_url: outcome.row.image_url ?? null,
              })}
              width={46} height={61} radius={6} onOpen={onImage}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {outcome.title && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px' }}>
                <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-.01em' }}>
                  {displayTitle(outcome.title)}
                </Typography>
                {outcome.row && <SeasonBadge value={outcome.row.season} size="xs" />}
                <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>
                  {outcome.sku}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontSize: 12.5, color: tone.colour, fontWeight: 700, lineHeight: 1.45, mt: outcome.title ? '2px' : 0 }}>
              {outcome.message}
            </Typography>
            {/* 訂得到嗎 on the added style, through the SAME isSourced the rows and the plan use —
                the 下單連結 and the 台灣倉庫 lane, never the 進貨店家 tag. MdoC is Taiwan warehouse
                stock and is never ordered from Korea, so it can no more read 訂不到 here than
                anywhere else on the screen. */}
            {outcome.row && outcome.kind === 'added' && (
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, mt: '2px', color: isSourced(outcome.row) ? C.go : C.back }}
              >
                {isSourced(outcome.row)
                  ? `訂得到${outcome.row.source_tag ? ` · ${outcome.row.source_tag}` : ''}`
                  : LIST_NO_LINK_WARNING}
              </Typography>
            )}
          </Box>
          {outcome.kind === 'added' && (
            <Box
              component="button" type="button" onClick={() => undo(outcome.sku)} disabled={!!busySku}
              aria-label={`${ADD_UNDO_LABEL} ${outcome.sku}`}
              sx={{
                flex: 'none', appearance: 'none', bgcolor: 'transparent', border: 0, p: 0,
                fontSize: 11.5, fontWeight: 800, color: C.back, cursor: 'pointer',
                textDecoration: 'underline', textUnderlineOffset: '2px',
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              {ADD_UNDO_LABEL}
            </Box>
          )}
          {outcome.kind === 'rejected' && (
            <Box
              component="button" type="button" onClick={() => addBack(outcome.sku)} disabled={!!busySku}
              aria-label={`${ADD_BACK_LABEL} ${outcome.sku}`}
              sx={{
                flex: 'none', appearance: 'none', cursor: 'pointer', borderRadius: '8px', px: 1.2,
                py: '6px', fontSize: 12.5, fontWeight: 800, border: '1px solid', borderColor: C.go,
                bgcolor: C.goWash, color: C.go, whiteSpace: 'nowrap',
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              {ADD_BACK_LABEL}
            </Box>
          )}
        </Box>
      )}

      {/* The secondary path: he typed a name, so the hits are the answer and each carries its own
          加入. Same endpoint, same rows, same button as before — only the way in changed. */}
      {outcome?.kind === 'hits' && !resolving && (
        <Box sx={{ mt: 1 }}>
          {writes.results.map((row) => (
            <SearchRow
              key={`hit:${row.sku}`} row={row} pending={!!writes.pending[row.sku]}
              onAdd={(sku) => { writes.add(sku); }} onImage={onImage}
            />
          ))}
        </Box>
      )}
      {outcome?.kind === 'hits' && !resolving && writes.results.length === 0 && (
        <Typography sx={{ fontSize: 13, color: C.ink2, px: '2px', mt: 1 }}>{LIST_SEARCH_EMPTY}</Typography>
      )}
    </Box>
  );
};

const ListScreen: React.FC<ListScreenProps> = ({
  plan, list, writes, pool, veto, desktop, wide, sourcing, onImage,
}) => {
  const [hintOpen, setHintOpen] = useState(true);
  // A freshly seeded rail produces ~20 建議加入 and ~37 建議換下. A queue that scrolls forever is
  // not answerable, so each shows a screenful until he asks for the rest.
  const [allAdds, setAllAdds] = useState(false);
  const [allDrops, setAllDrops] = useState(false);
  const [allStock, setAllStock] = useState(false);

  // The table is missing (or the backend has not shipped the block yet): say so in one line and
  // leave the other three screens alone. Nothing here throws on a null `list`.
  if (!list || list.ready !== true) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info" sx={{ mb: 1.5 }}>{list?.message || LIST_NOT_READY}</Alert>
        <Typography sx={{ fontSize: 13, color: C.ink2 }}>
          等 `noke_counter_list` 上線後，引擎的建議會出現在這裡，你按 yes／no，清單就變成門市的預設。
        </Typography>
      </Box>
    );
  }

  // Split, not merged: 你已經有貨 is 「I own these, put them out?」 and 建議加入 is 「the engine
  // suggests this」. Different questions, different money, so they never share a queue or a cap.
  const adds = engineProposals(list);
  const owned = stockProposals(list);
  const drops = proposalsToRemove(list);
  const approved = approvedRows(list);
  const groups = groupApprovedByFixture(approved);
  const hints = bringBackHints(list);
  const over = isOverCapacity(list);
  const overText = overLine(list);
  // NOT the stat tile's sentence again: the tile is the number, this is the move it implies.
  const gapLine = seasonActionLine(list);
  const rejected = excludedStyles(plan);
  // 不要這個顏色／尺寸 is its OWN list in 已排除, never merged into the 款 rows: 加回 on a style and
  // 加回 on a colour undo different things, and the count 「6 款」 would be false if it counted both.
  const rejectedVariants = excludedVariants(list);
  const variantsOn = variantsReady(list);
  const missing = missingSkus(list);
  const shownAdds = cappedQueue(adds, allAdds);
  const shownOwned = cappedQueue(owned, allStock);
  const shownDrops = cappedQueue(drops, allDrops);
  // Styles already answered ELSEWHERE on this screen — approved (我的清單) or rejected (已排除).
  // The pool is a snapshot taken when he opened it, so a style he adds from 建議加入 or the picker
  // would otherwise still be sitting in it offering the same decision twice. This set only ever
  // removes pool rows; the pool's own `decided` set is what keeps an answer MADE there from
  // reappearing when a recompute lands.
  const decidedSkus: Record<string, true> = {};
  approved.forEach((row) => { decidedSkus[row.sku] = true; });
  rejected.forEach((row) => { decidedSkus[row.sku] = true; });
  const columns = wide ? 3 : (desktop ? 2 : 1);
  const busy = (sku: string) => !!writes.pending[sku];
  const variantBusy = (sku: string, variantId: number) => !!veto.pending[variantPendingKey(sku, variantId)];
  // Per STYLE, not per chip: one pin per style means two 主打 taps race for the same row.
  // Per CHIP: a write on 黑色 must not grey out 海軍藍's button on the same row. The
  // style-wide key is still checked because 取消主打 with no variant clears them all.
  // Styles that would GAIN a 主打 from one tap. Computed from the SERVED rows, so the button's
  // count is the same set the write sends — a count derived some other way is how a button ends
  // up promising 40 and writing 37.
  const mainPicks = mainBatchPicks(approved);
  const mainStyleCount = new Set(mainPicks.map((pick) => pick.sku)).size;
  const mainBusy = (sku: string) => (variantId: number | string | null) => (
    !!veto.pending[variantMainPendingKey(sku, variantId)] || !!veto.pending[variantMainPendingKey(sku)]);
  // HiddenFold keys its 加回 by planVetoKey (it is shared with the three veto screens), so the
  // SKU-keyed pending map is re-keyed rather than teaching the fold a second convention. The
  // variant keys are already in the fold's own namespace, so they pass through untouched.
  const foldPending = Object.keys({ ...writes.pending, ...veto.pending })
    .reduce<Record<string, boolean>>((out, key) => {
      out[key.indexOf('variant|') === 0 ? key : planVetoKey(key)] = true;
      return out;
    }, {});

  return (
    <Box sx={{ pb: 3 }}>
      <StatStrip
        cells={[
          { label: '清單', value: list.counts?.approved ?? 0, suffix: '款' },
          { label: '計畫件數', value: plannedLine(list), suffix: '件', highlight: over },
          { label: '待決定', value: list.counts?.pending ?? 0, suffix: '款' },
          {
            label: '清單秋冬',
            value: `${list.season?.aw_share_pct ?? 0}%`,
            suffix: `目標 ${list.season?.aw_target_pct ?? 0}%`,
          },
        ]}
      />

      {/* 「訂貨：3 要訂、1 倉庫、1 沒連結、1 已經夠了」 — the same accounting 訂貨 draws in full,
          said in one line HERE, because this is the screen he is on when he counts the 清單 and
          starts wondering where the rest of it went. He asked that three times; answering it only
          after he switches tabs still makes him go looking. The words and the numbers are the
          server's (order.accounting) — this restates nothing, so the two screens cannot disagree.
          Deliberately one line: 選品 is already the densest screen in the tab, and the openable
          version belongs on 訂貨, where the buckets are the work. */}
      {accountingListLine(plan) && (
        <Typography
          role="note" aria-label="訂貨去向"
          sx={{ fontSize: 12.5, color: C.ink2, fontWeight: 700, px: '2px', mb: 1.5, mt: -0.5 }}
        >
          {accountingListLine(plan)}
        </Typography>
      )}

      {/* Over capacity NEVER blocks and NEVER drops anything — it is a number plus a ranked hint. */}
      {over && (
        <Box
          role="note" aria-label="超出容量"
          sx={{
            bgcolor: C.backWash, border: '1px solid', borderColor: C.back, borderRadius: `${RADIUS}px`,
            px: 1.25, py: 1, mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: C.back }}>{overText}</Typography>
            <Typography sx={{ fontSize: 12, color: C.ink2 }}>
              清單還是照你決定的出，沒有自動刪掉任何一款。下面是引擎建議先帶回的順序。
            </Typography>
            {hints.length > 0 && (
              <Box
                component="button" type="button" onClick={() => setHintOpen((old) => !old)}
                aria-expanded={hintOpen} aria-label="建議帶回順序"
                sx={{
                  ml: 'auto', appearance: 'none', background: 'none', border: 0, p: 0, fontSize: 11.5,
                  fontWeight: 800, color: C.back, cursor: 'pointer', textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                {hintOpen ? '收起' : '查看'}
              </Box>
            )}
          </Box>
          {hintOpen && hints.map((hint, index) => (
            <Box
              key={`hint:${hint.sku}`} data-bring-back={hint.sku}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, minWidth: 0 }}
            >
              <Typography
                component="span"
                sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: '#fff', bgcolor: C.back, borderRadius: '4px', px: '5px', flex: 'none' }}
              >
                {index + 1}
              </Typography>
              <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, minWidth: 0 }}>
                {displayTitle(hint.title)}
              </Typography>
              <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{hint.sku}</Typography>
              <Typography component="span" sx={{ fontSize: 11.5, color: C.ink2 }}>{hint.reason}</Typography>
              <Typography
                component="span"
                sx={{ ml: 'auto', fontFamily: MONO, fontSize: 11.5, color: C.back, fontWeight: 700, flex: 'none' }}
              >
                {hint.pieces} 件
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {gapLine && (
        <Typography
          role="note"
          sx={{
            fontSize: 12.5, color: C.wait, fontWeight: 700, bgcolor: C.waitWash, borderRadius: '8px',
            px: 1, py: 0.6, mb: 1.5,
          }}
        >
          {gapLine}
        </Typography>
      )}

      {/* ---- 2. 加入商品 — HIS move, above the engine's four queues ------------------------------
           Magnus 2026-09-18: 「at the 選品 tab make it allow me to enter sku, to 加入清單 manually」.
           It was already on the screen, at the very bottom, and he could not find it. Everything
           below this point is the engine's agenda (你已經有貨 / 建議加入 / 看更多 / 建議換下 /
           我的清單 / 已排除); this is the one control that needs no proposal first, so it goes
           directly under the header numbers and notes. */}
      <AddBySku writes={writes} desktop={desktop} onImage={onImage} />

      {/* ---- 3a. 你已經有貨 — pieces he already paid for, first, because the gate now suppresses
           their 上架 / 去拿 rows entirely: unseen here means unseen anywhere. --------------- */}
      {owned.length > 0 && (
        <Box component="section" aria-label={LIST_STOCK_TITLE}>
          <Head title={LIST_STOCK_TITLE} note={`${owned.length} 款`} />
          <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9 }}>{LIST_STOCK_NOTE}</Typography>
          <Grid columns={columns}>
            {shownOwned.map((row) => (
              <ListProposalCard
                key={`stock:${row.sku}`} row={row} kind="stock" pending={busy(row.sku)}
                onApprove={(item) => writes.decide(item.sku, 'approve', item.tier, proposalEvidence(item, 'stock'))}
                onDecline={(item) => writes.decide(item.sku, 'reject', item.tier, proposalEvidence(item, 'stock'))}
                onImage={onImage}
              />
            ))}
          </Grid>
          <MoreButton
            hidden={allStock || shownOwned.length >= owned.length} total={owned.length}
            onClick={() => setAllStock(true)}
          />
        </Box>
      )}

      {/* ---- 3b. the engine's yes/no queue ------------------------------------------------ */}
      <Box component="section" aria-label="建議加入">
        <Head title="建議加入" note={adds.length ? `${adds.length} 款` : undefined}>
          {adds.length > 0 && (
            <Box
              component="button" type="button" onClick={() => writes.decideAll(adds)}
              disabled={!!writes.pending[BATCH_KEY]} aria-label={`${LIST_BATCH_LABEL} ${adds.length} 款`}
              sx={{
                ml: 'auto', appearance: 'none', cursor: 'pointer', borderRadius: '9px', px: 1.4,
                py: '8px', fontSize: 13, fontWeight: 800, border: 0, bgcolor: C.go, color: '#fff',
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              {LIST_BATCH_LABEL}
            </Box>
          )}
        </Head>
        {adds.length > 0 && (
          <Typography sx={{ fontSize: 11.5, color: C.ink3, px: '2px', pb: 0.9 }}>{LIST_BATCH_NOTE}</Typography>
        )}
        {adds.length === 0 && drops.length === 0 && owned.length === 0 && (
          <Typography sx={{ fontSize: 13, color: C.ink2, px: '2px' }}>{LIST_NO_PROPOSALS}</Typography>
        )}
        <Grid columns={columns}>
          {shownAdds.map((row) => (
            <ListProposalCard
              key={`add:${row.sku}`} row={row} kind="add" pending={busy(row.sku)}
              onApprove={(item) => writes.decide(item.sku, 'approve', item.tier, proposalEvidence(item, 'add'))}
              onDecline={(item) => writes.decide(item.sku, 'reject', item.tier, proposalEvidence(item, 'add'))}
              onImage={onImage}
            />
          ))}
        </Grid>
        <MoreButton
          hidden={allAdds || shownAdds.length >= adds.length} total={adds.length}
          onClick={() => setAllAdds(true)}
        />
      </Box>

      {/* ---- 3c. 看更多 — 引擎沒選的 ------------------------------------------------------------
           Directly under 建議加入 because it answers the complaint 建議加入 caused (「only have 2
           items … i need more」), and CLOSED by default because hundreds of below-the-bar rows
           between him and 我的清單 would be a worse screen than the one he complained about. */}
      <PoolSection pool={pool} hidden={decidedSkus} columns={columns} onImage={onImage} />

      {drops.length > 0 && (
        <Box component="section" aria-label="建議換下">
          <Head title="建議換下" note={`${drops.length} 款`} />
          <Typography sx={{ fontSize: 11.5, color: C.ink3, px: '2px', pb: 0.9 }}>
            這些還在你的清單上，引擎建議換下來。按「留著」就留著，引擎不會再吵。
          </Typography>
          <Grid columns={columns}>
            {shownDrops.map((row) => (
              <ListProposalCard
                key={`drop:${row.sku}`} row={row} kind="remove" pending={busy(row.sku)}
                onApprove={(item) => writes.decide(item.sku, 'approve', item.tier, proposalEvidence(item, 'remove'))}
                onDecline={(item) => writes.remove(item.sku)}
                onImage={onImage}
              />
            ))}
          </Grid>
          <MoreButton
            hidden={allDrops || shownDrops.length >= drops.length} total={drops.length}
            onClick={() => setAllDrops(true)}
          />
        </Box>
      )}

      {/* ---- 4. 我的清單 ----------------------------------------------------------------- */}
      <Box component="section" aria-label="我的清單">
        {/* 「連結拿不到」 said ONCE for the screen, with its own retry, exactly as 訂貨 says it —
            a per-row error on 80 rows would be 80 copies of one fact. */}
        <SinsangNotice sourcing={sourcing} />
        <Head
          title="我的清單" note={`${approved.length} 款`}
          count={`${list.counts?.on_rail ?? 0} 在櫃 · ${list.counts?.off_rail ?? 0} 不在櫃`}
        >
          {/* 照建議設定主打 — Magnus 2026-09-20: 「just set it automatically. i will change if
              needed」. One write for every style that has no 主打 yet; the ones he has already
              decided are not in `mainPicks` at all, so the button cannot overwrite a decision.
              Hidden entirely when there is nothing left to fill in, rather than sitting there
              disabled — a dead button on a dense screen reads as a broken one. */}
          {veto.mainsReady && mainPicks.length > 0 && (
            <Box
              component="button" type="button" data-main-batch
              disabled={writes.pending[MAIN_BATCH_KEY]}
              onClick={() => writes.setMainsFromSuggestion(mainPicks)}
              title={MAIN_BATCH_NOTE}
              sx={{
                appearance: 'none', cursor: 'pointer', border: '1px solid', borderColor: C.line,
                bgcolor: C.card, borderRadius: '8px', px: '9px', py: '3px', fontSize: 12,
                fontWeight: 800, color: C.ink2, whiteSpace: 'nowrap',
                '&:hover': { bgcolor: C.goWash, color: C.go, borderColor: C.go },
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              {MAIN_BATCH_LABEL} {mainStyleCount} 款
            </Box>
          )}
        </Head>
        {missing.length > 0 && (
          <Typography role="note" sx={{ fontSize: 12, color: C.back, fontWeight: 700, px: '2px', pb: 0.9 }}>
            清單裡有 {missing.length} 款已不在目錄：{missing.join('、')}
          </Typography>
        )}
        {/* One line, and only when there is something to act on: the ✕ on a chip is a new verb and
            「不要這款 removes every colour」 is exactly what he complained about. */}
        {variantsOn && approved.some((row) => listVariants(row).length > 0) && (
          <Typography sx={{ fontSize: 11.5, color: C.ink3, px: '2px', pb: 0.9, lineHeight: 1.45 }}>
            {LIST_VARIANT_NOTE}
          </Typography>
        )}
        {approved.length === 0 ? (
          <Box>
            <Typography sx={{ fontSize: 13, color: C.ink2, px: '2px', pb: 1 }}>{LIST_EMPTY_NOTE}</Typography>
            <Box
              component="button" type="button" onClick={() => writes.seed()}
              disabled={!!writes.pending[SEED_KEY]} aria-label={LIST_SEED_LABEL}
              sx={{
                appearance: 'none', cursor: 'pointer', borderRadius: '10px', px: 1.8, py: 1.1,
                fontSize: 14, fontWeight: 800, border: 0, bgcolor: C.ink, color: C.onInk,
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              {LIST_SEED_LABEL}
            </Box>
          </Box>
        ) : (
          groups.map((group) => (
            <Box key={group.id} component="section" aria-label={fixtureLabelOf(plan, group.id)} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.6 }}>
                <Typography component="h4" sx={{ fontSize: 13, fontWeight: 800, color: C.ink2 }}>
                  {fixtureLabelOf(plan, group.id)}
                </Typography>
                <Typography
                  sx={{ ml: 'auto', fontFamily: MONO, fontSize: 11.5, color: C.ink3 }}
                >
                  {group.rows.length} 款 · {group.pieces} 件
                </Typography>
              </Box>
              <Grid columns={columns}>
                {group.rows.map((row) => (
                  <ApprovedRow
                    key={`approved:${row.sku}`} row={row} pending={busy(row.sku)}
                    onRemove={(sku) => writes.remove(sku)}
                    variantsReady={variantsOn} variantBusy={variantBusy}
                    onRejectVariant={(item, variant) => veto.onVariant(item.sku, variant)}
                    onReaddVariant={(item, variant) => veto.onReadd(item.sku, variant.variant_id)}
                    mainsReady={veto.mainsReady} mainBusy={mainBusy(row.sku)}
                    pinnedIds={veto.mainPinned(row.sku, mainVariantIds(row))}
                    onMainVariant={(item, variant) => veto.onMain(item.sku, variant)}
                    onUnmainVariant={(item, variant) => veto.onUnmain(item.sku, variant)}
                    sourcing={sourcing} onImage={onImage}
                  />
                ))}
              </Grid>
            </Box>
          ))
        )}
      </Box>

      {/* ---- 5. 已排除 — 不要這款 is now a `rejected` row on the list; 加回 deletes it, which puts
           the style back to 待決定 so the engine may propose it again. ---------------------- */}
      <Box component="section" aria-label="已排除">
        <Head
          title="已排除"
          note={rejectedVariants.length > 0
            ? `${rejected.length} 款 · ${rejectedVariants.length} 個顏色／尺寸`
            : `${rejected.length} 款 · 引擎不會再建議`}
        />
        {rejectedVariants.length > 0 && (
          <Typography sx={{ fontSize: 11.5, color: C.ink3, px: '2px', pb: 0.9 }}>
            {LIST_VARIANT_EXCLUDED_NOTE}
          </Typography>
        )}
        {rejected.length === 0 && rejectedVariants.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: C.ink2, px: '2px' }}>還沒有排除任何一款。</Typography>
        ) : (
          <HiddenFold
            label={rejectedVariants.length > 0
              ? `已排除 ${rejected.length} 款 · ${rejectedVariants.length} 個顏色／尺寸 · 查看`
              : `已排除 ${rejected.length} 款 · 查看`}
            styles={rejected} variants={rejectedVariants} pending={foldPending}
            onAddBackStyle={(sku) => writes.remove(sku)}
            onAddBackVariant={(row) => veto.onReadd(row.sku, row.variant_id)}
          />
        )}
      </Box>
    </Box>
  );
};

export default ListScreen;
